import React, { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import rehypeKatex from "rehype-katex";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { tomorrow } from "react-syntax-highlighter/dist/esm/styles/prism";
import { prepareMixedMarkdownForRender } from "../../utils/markdownRender";

const sanitizeSchema = {
  ...defaultSchema,
  tagNames: [...new Set([...(defaultSchema.tagNames || []), "div"])],
  attributes: {
    ...defaultSchema.attributes,
    a: [
      ...(defaultSchema.attributes?.a || []),
      ["className", "sight-link-name", "hotel-link-name"],
    ],
    div: [
      [
        "className",
        "image-wrapper1",
        "image-wrapper4",
        /^image-num[1-9]$/,
      ],
    ],
    code: [
      ...(defaultSchema.attributes?.code || []),
      ["className", /^language-./, "math-inline", "math-display"],
    ],
  },
  protocols: {
    ...defaultSchema.protocols,
    href: ["http", "https"],
    src: ["http", "https"],
  },
};

const remarkPlugins = [remarkGfm, remarkMath];
const rehypePlugins = [
  [rehypeRaw, { tagfilter: true }],
  [rehypeSanitize, sanitizeSchema],
  rehypeKatex,
];

const MarkdownCodeBlock = ({ className = "", children }) => {
  const match = /language-(\w+)/.exec(className);
  return match ? (
    <SyntaxHighlighter style={tomorrow} language={match[1]} PreTag="div">
      {String(children).replace(/\n$/, "")}
    </SyntaxHighlighter>
  ) : (
    <code className={className}>{children}</code>
  );
};

const components = {
  code: MarkdownCodeBlock,
  a: ({ href, title, className, children }) => (
    <a
      href={href}
      title={title}
      className={className}
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  ),
  table: ({ children }) => (
    <div className="markdown-table-wrap">
      <table>{children}</table>
    </div>
  ),
  img: ({ src = "", alt = "", title }) =>
    src ? (
      <img
        src={src}
        alt={alt}
        title={title}
        loading="eager"
        decoding="async"
      />
    ) : null,
  input: ({ type, checked }) =>
    type === "checkbox" ? (
      <input
        type="checkbox"
        checked={Boolean(checked)}
        readOnly
        disabled
        className="markdown-task-checkbox"
      />
    ) : (
      <input type={type} />
    ),
};

const MarkdownContent = React.memo(function MarkdownContent({
  content = "",
  dir = "ltr",
  className = "agentMessage_content",
}) {
  const renderedContent = useMemo(
    () => prepareMixedMarkdownForRender(content),
    [content],
  );

  return (
    <div className={className} dir={dir}>
      <ReactMarkdown
        remarkPlugins={remarkPlugins}
        rehypePlugins={rehypePlugins}
        components={components}
      >
        {renderedContent}
      </ReactMarkdown>
    </div>
  );
});

export default MarkdownContent;
